import React from 'react';

import { ReactComponent as Scissors } from '@/assets/icons/scissors.svg';

import { FeedbackGraphic } from '@/components/FeedbackGraphic';
import InputField from '@/components/InputField';
import Spinner from '@/components/Spinner';

import { Solicitacao } from '@/models/Solicitacao';

import InputFieldQuantity from '@/features/recursos/procedimentos/components/InputFieldQuantity';
import { codeableConceptDisplay } from '@/features/fhir/helpers/fhirpath';
import {
  deviceName,
  deviceQuantityValue,
} from '@/features/fhir/helpers/device';

import {
  Container,
  Heading,
  LoadingContent,
  LoadingText,
  OPMEBox,
  OPMEBoxContent,
  OPMEBoxFeedback,
  OPMEGroup,
} from './styles';
import { GroupedDevices, groupSharedDevicesByProcedureIds } from './helpers';

interface Props {
  procedimentos: Solicitacao[];
}

const ListaOPMESugerido: React.FC<Props> = ({ procedimentos }) => {
  const opmeBoxRef = React.useRef<HTMLDivElement>(null);

  const serviceRequests = React.useMemo(() => {
    return procedimentos.map(
      procedimento => procedimento.pedido as fhir4.ServiceRequest,
    );
  }, [procedimentos]);

  const [groups, setGroups] = React.useState<GroupedDevices[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [loadingMinHeight, setLoadingMinHeight] = React.useState<number>(180);

  const emptyBoxText = React.useMemo(() => {
    if (serviceRequests.length === 0) {
      return 'As OPMEs serão sugeridas após a seleção dos procedimentos.';
    }

    if (groups.length === 0) {
      return 'Nenhuma OPME sugerida para os procedimentos selecionados.';
    }

    return '';
  }, [serviceRequests.length, groups.length]);

  React.useEffect(() => {
    let isCancelled = false;
    const loadingStart = Date.now();
    const currentBoxHeight = opmeBoxRef.current?.offsetHeight;

    if (currentBoxHeight) {
      setLoadingMinHeight(currentBoxHeight);
    }

    setIsLoading(true);

    const grouped = groupSharedDevicesByProcedureIds(serviceRequests);
    const loadingRemaining = Math.max(300 - (Date.now() - loadingStart), 0);

    const timeoutId = window.setTimeout(() => {
      if (isCancelled) {
        return;
      }

      setGroups(grouped);
      setIsLoading(false);
    }, loadingRemaining);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [serviceRequests]);

  const groupTitle = React.useCallback(
    (group: GroupedDevices) => {
      if (group.procedureIds.length > 0) {
        return serviceRequests
          .filter(
            request => request.id && group.procedureIds.includes(request.id),
          )
          .map(request => codeableConceptDisplay(request, 'code'))
          .join(', ');
      }

      return '';
    },
    [serviceRequests],
  );

  let opmeContent = (
    <OPMEBoxContent>
      {groups.map(group => (
        <OPMEGroup key={group.procedureIds.join('|')}>
          <thead>
            <tr>
              <td>{groupTitle(group)}</td>
              <td>Quantidade</td>
            </tr>
          </thead>
          <tbody>
            {group.devices.map(device => (
              <tr key={[...group.procedureIds, device.id].join('|')}>
                <td>
                  <InputField readOnly value={deviceName(device)} />
                </td>
                <td>
                  <InputFieldQuantity
                    // Prop temporária enquanto manipulação ainda não foi implementada
                    readOnly
                    minValue={deviceQuantityValue(device, 'min-quantity') || 1}
                    maxValue={deviceQuantityValue(device, 'max-quantity')}
                    initialValue={deviceQuantityValue(device, 'quantity') || 1}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </OPMEGroup>
      ))}
    </OPMEBoxContent>
  );

  if (isLoading) {
    opmeContent = (
      <OPMEBoxContent>
        <LoadingContent style={{ minHeight: loadingMinHeight }}>
          <Spinner size={24} />
          <LoadingText>Carregando OPME</LoadingText>
        </LoadingContent>
      </OPMEBoxContent>
    );
  } else if (groups.length === 0) {
    opmeContent = (
      <OPMEBoxFeedback>
        <FeedbackGraphic graphic="empty-box" title={emptyBoxText} />
      </OPMEBoxFeedback>
    );
  }

  return (
    <Container>
      <Heading>
        <Scissors />
        OPMEs sugeridas
      </Heading>

      <OPMEBox ref={opmeBoxRef}>{opmeContent}</OPMEBox>
    </Container>
  );
};

export default ListaOPMESugerido;
